import type { ChangeEvent, ComponentProps } from 'react';
import React, { useState } from 'react';
import styled from '@emotion/styled';
import type {
  BorderRadiusProps,
  PositionProps,
  RightProps,
  TopProps,
  WidthProps,
} from 'styled-system';
import { borderRadius, position, right, space, top, width } from 'styled-system';

import type { HTMLAutoCompleteAttribute } from '../../enums/form';
import { FieldChangeTrigger, HTMLInputFieldType } from '../../enums/form';
import type { PasswordHelpMessagesConfigFragment } from '../../gql/generated/schema';
import { getPasswordFieldHelpMessages } from '../../hooks/password-field';
import { useLocalization } from '../../providers/LocaleProvider';
import type { SpaceProps } from '../../theme/system-types';
import type { FieldChangeTriggerHandler } from '../../types/form';
import { Button } from '../Button';
import Icon from '../Icon';
import Input from '../Input/StatefulInput';
import { HeadlineH7 } from '../Typography';

export interface PasswordFieldProps<FieldName extends string = string> {
  label: string;
  name: FieldName;
  disableMessages?: boolean;
  autoComplete?: HTMLAutoCompleteAttribute;
  hasError?: boolean;
  required?: boolean;
  disabled?: boolean;
  onChange: FieldChangeTriggerHandler<FieldName>;
  value?: string;
  title?: string;
  errors?: string[];
  helpMessagesConfig?: PasswordHelpMessagesConfigFragment[];
}

const ICON_SIZE = 24;
const EMPTY_LIST = [];
const MAX_LENGTH_PASSWORD_FIELD = 72;

const PasswordInput = styled(Input)<SpaceProps>`
  ${space}

  &::-ms-clear {
    display: none;
  }
`;

const Wrapper = styled.div<WidthProps & PositionProps>`
  ${width};
  ${position};
`;

const IconButtonBase = styled(Button)<PositionProps & TopProps & RightProps & BorderRadiusProps>`
  ${position};
  ${top};
  ${right};
  ${borderRadius};
  user-select: none;

  &:focus-visible {
    border-radius: 100%;
  }
`;

const IconButton = (props: ComponentProps<typeof IconButtonBase>) => (
  <IconButtonBase
    kind="bare"
    top={['9px', null, null, '11px']}
    right="8px"
    position="absolute"
    borderRadius="circle"
    {...props}
  />
);

const PasswordField = ({
  title,
  label,
  name,
  disableMessages,
  helpMessagesConfig,
  autoComplete,
  hasError,
  required,
  disabled,
  onChange,
  errors,
  value: initialValue = '',
}: PasswordFieldProps) => {
  const { t } = useLocalization();
  const [passwordValue, setPasswordValue] = useState(initialValue);
  const [fieldType, setFieldType] = useState(HTMLInputFieldType.Password);

  const handleChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    const { value } = target;
    setPasswordValue(value);
    onChange({
      fieldName: name,
      value,
      trigger: FieldChangeTrigger.Change,
    });
  };

  const handleBlur = () => {
    onChange({
      fieldName: name,
      value: passwordValue,
      trigger: FieldChangeTrigger.Blur,
    });
  };

  const stateMessagesProps = disableMessages
    ? EMPTY_LIST
    : getPasswordFieldHelpMessages({
        value: passwordValue,
        helpMessagesConfig,
      });

  return (
    <>
      {!!title && <HeadlineH7 mb="sp_16">{title}</HeadlineH7>}
      <Wrapper width={1} position="relative">
        <PasswordInput
          name={name}
          type={fieldType}
          aria-label={label}
          aria-invalid={hasError}
          aria-required={required ?? false}
          hasError={hasError}
          placeholder={label}
          label={label}
          withIcon
          withMessagesIcon={!disableMessages}
          value={passwordValue}
          disabled={disabled}
          maxLength={MAX_LENGTH_PASSWORD_FIELD}
          onChange={handleChange}
          onBlur={handleBlur}
          messages={stateMessagesProps}
          errors={disableMessages ? errors : EMPTY_LIST}
          mb={disableMessages ? 'sp_0' : ['sp_4', null, 'sp_8']}
          {...(autoComplete ? { autoComplete } : {})}
        />
        {fieldType === HTMLInputFieldType.Text ? (
          <IconButton
            data-testid="mask-password"
            onClick={() => setFieldType(HTMLInputFieldType.Password)}
            aria-label={t('h24_hide_password')}
          >
            <Icon name="invisible" size={ICON_SIZE} />
          </IconButton>
        ) : (
          <IconButton
            data-testid="unmask-password"
            onClick={() => setFieldType(HTMLInputFieldType.Text)}
            aria-label={t('h24_show_password')}
          >
            <Icon name="visible" size={ICON_SIZE} />
          </IconButton>
        )}
      </Wrapper>
    </>
  );
};

export default PasswordField;
